import { useMutation, useQuery } from "@apollo/client";
import {
  BaseTable,
  Button,
  Flexbox,
  Select,
  SingleDatePicker
} from "@sede-x/shell-ds-react-framework";
import dayjs from "dayjs";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import {
  GqlResponse,
  MovementDeliveryEventStatus,
  Picklists,
  TDeal,
  TMovement,
  TMovementGroup,
  TPicklists
} from "ticketing/ticketing.types";
import { fromISODateString, isPhysicalDeal, toISODateString } from "ticketing/utils";

type DropDownListCellProps = {
  row: { original: TMovementDeliveryEventGroup };
  column: { id: string };
  onChange: (updatedRow: TMovementDeliveryEventGroup) => void;
  data?: Picklists.PDeliveryEventType[] | "STATUS";
};
type DeliveryEventValue = {
  id: string | number;
  name: string;
};

type CellProps = {
  row: { original: TMovementDeliveryEventGroup };
  column: { id: string };
};

const DropDownListCell = ({ row, column, onChange, data }: DropDownListCellProps) => {
  const isInEdit = row.original.inEdit; // Check if the row is in edit mode
  const field = column.id; // Get the field name from the column
  const value = row.original[field as keyof TMovementDeliveryEventGroup]; // Get the current value

  // Determine the options based on the data prop
  const isStatusType = typeof data === "string" && data === "STATUS";
  const options = isStatusType
    ? row.original.statuses || []
    : (data as Picklists.PDeliveryEventType[]) || [];

  const handleChange = (selectedValue: string | number) => {
    const selected = options.find(item => String(item.id) === String(selectedValue));

    if (selected) {
      // Update with the found option
      onChange({
        ...row.original,
        [field]: selected
      });
    }
  };

  return (
    <td>
      {isInEdit ? (
        <Select
          value={(value as DeliveryEventValue)?.id || ""}
          onChange={handleChange}
          style={{ width: "100%" }}
          allowClear={false}
          getPopupContainer={() => document.querySelector(".movement-aspect") || document.body}>
          {options.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Select>
      ) : (
        <span>{(value as DeliveryEventValue)?.name || "N/A"}</span>
      )}
    </td>
  );
};

type DatePickerCellProps = {
  row: { original: TMovementDeliveryEventGroup };
  column: { id: string };
  onChange: (updatedRow: TMovementDeliveryEventGroup) => void;
};

const DatePickerCell = ({ row, column, onChange }: DatePickerCellProps) => {
  const isInEdit = row.original.inEdit;
  const field = column.id;
  const value = row.original[field as keyof TMovementDeliveryEventGroup];

  const handleChange = (dateObj: dayjs.Dayjs | null) => {
    const dateValue = dateObj ? dateObj.toDate() : null;

    onChange({
      ...row.original,
      [field]: dateValue
    });
  };

  return (
    <td>
      {isInEdit ? (
        <SingleDatePicker
          allowClear={false}
          value={value ? dayjs(value as Date) : null}
          onChange={handleChange}
          popupStyle={{ position: "fixed", top: "auto", left: "auto" }}
          showToday={false}
        />
      ) : (
        <span>{value ? new Date(value as Date).toLocaleDateString() : "N/A"}</span>
      )}
    </td>
  );
};

const QUERY_MOVEMENT_DELIVERY_EVENTS = loader(
  "../../ticketing-graphql/deliveryEventsOfMovement.graphql"
);

const UPDATE_MOVEMENT_DELIVERY_EVENTS = loader(
  "../../ticketing-graphql/updateDeliveryEventsOfMovements.graphql"
);

type Response = GqlResponse<Partial<TMovement[]>, "movements">;

type TMovementDeliveryEventId = {
  id?: string;
  version?: number;
  movementId?: string;
  movementVersion?: number;
};

type TMovementDeliveryEventGroup = {
  id: string;
  deliveryEventType?: Picklists.PDeliveryEventType;
  deliveryEventDate?: Date;
  deliveryEventStatus?: TDeliveryEventStaus;
  movementDeliveryEventId?: [TMovementDeliveryEventId];
  statuses: TDeliveryEventStaus[];
  inEdit: boolean;
};

type TDeliveryEventUpdate = {
  id: string;
  version: number;
  deliveryEventTypeId: number;
  deliveryEventDate: string;
  deliveryEventStatus: MovementDeliveryEventStatus;
};

type TDeliveryEventInputUpdate = {
  id: string;
  version: number;
  deliveryEvents: TDeliveryEventUpdate[];
};

const isRequiredForPricing = (deal: TDeal, id: number) =>
  deal.deliveryEvents.find(d => d.deliveryEventType?.id === id)?.requiredForPricing ?? false;

type TDeliveryEventStaus = {
  id: string;
  name: string;
};
const defaultDeliveryEventStauses: TDeliveryEventStaus[] = [
  { id: "ESTIMATED", name: "UNLINKED" }
];
const finalDeliveryEventStauses: TDeliveryEventStaus[] = defaultDeliveryEventStauses.concat({
  id: "FINAL",
  name: "FINAL"
});

const getStatuses = (m: TMovement, id: number): TDeliveryEventStaus[] =>
  (isPhysicalDeal(m.delDeal) && isRequiredForPricing(m.delDeal, id)) ||
  (isPhysicalDeal(m.recDeal) && isRequiredForPricing(m.recDeal, id))
    ? finalDeliveryEventStauses
    : defaultDeliveryEventStauses;

const transformInput = (movements: Partial<TMovement[]>): TMovementDeliveryEventGroup[] => {
  //flatten all delivery events from all sides of the movement
  const deliveryEvents = movements.flatMap(md =>
    md?.deliveryEvents.map(de => ({
      ...de,
      deliveryEventDate: fromISODateString(de.deliveryEventDate),
      deliveryEventStatus: finalDeliveryEventStauses.find(
        s => s.id === de.deliveryEventStatus.toString()
      ),
      movementId: md.id,
      movementVersion: md.version,
      statuses: getStatuses(md, de.deliveryEventType?.id!)
    }))
  );

  //group delivery events by delivery event type id
  const delivertEventGroups = deliveryEvents.reduce<
    Record<string, TMovementDeliveryEventGroup>
  >((group, item) => {
    const deliveryEventTypeId: string = `Id:${item!.deliveryEventType!.id}`;
    if (group[deliveryEventTypeId] == null) {
      group[deliveryEventTypeId] = {
        id: deliveryEventTypeId,
        deliveryEventType: item?.deliveryEventType,
        deliveryEventStatus: item?.deliveryEventStatus,
        deliveryEventDate: item?.deliveryEventDate,
        statuses: item?.statuses!,
        movementDeliveryEventId: [
          {
            id: item?.id,
            version: item?.version,
            movementId: item?.movementId,
            movementVersion: item?.movementVersion
          }
        ],
        inEdit: true
      };
      return group;
    }
    group[deliveryEventTypeId].movementDeliveryEventId?.push({
      id: item?.id,
      version: item?.version,
      movementId: item?.movementId,
      movementVersion: item?.movementVersion
    });

    return group;
  }, {});
  return Object.values(delivertEventGroups);
};

const transformOutput = (movementDeliveryEvents: TMovementDeliveryEventGroup[]) => {
  //flatten to TMovementDeliveryEventGroup
  const movementDeliveryEventUpdate = movementDeliveryEvents?.flatMap(d =>
    d.movementDeliveryEventId?.map(md => ({
      id: md.movementId,
      version: md.movementVersion,
      deliveryEvents: {
        id: md.id!,
        version: md.version!,
        deliveryEventTypeId: d.deliveryEventType?.id!,
        deliveryEventDate: toISODateString(d.deliveryEventDate!),
        deliveryEventStatus: d.deliveryEventStatus
          ?.id! as unknown as MovementDeliveryEventStatus
      }
    }))
  );

  //group them by movement id
  const movementDeliveryEventUpdateGroup = movementDeliveryEventUpdate?.reduce<
    Record<string, TDeliveryEventInputUpdate>
  >((movementIdGroup, item) => {
    if (movementIdGroup[item!.id!] == null) {
      movementIdGroup[item!.id!] = {
        id: item?.id!,
        version: item?.version!,
        deliveryEvents: [item?.deliveryEvents!]
      };
      return movementIdGroup;
    }
    movementIdGroup[item!.id!].deliveryEvents.push(item?.deliveryEvents!);
    return movementIdGroup;
  }, {});

  return Object.values(movementDeliveryEventUpdateGroup);
};

type MovementDeliveryEventsProps = {
  movementGroup: TMovementGroup;
  editDisabled: boolean;
  onClose: () => void;
};
/**
 *
 * @param param0
 * @returns
 */
const makeColumns = (
  picklists: TPicklists,
  handleItemChange: (updatedRow: TMovementDeliveryEventGroup) => void
) => [
  {
    header: "Delivery Event Type",
    accessorKey: "deliveryEventType",
    cell: (props: CellProps) => (
      <DropDownListCell
        row={props.row}
        column={props.column}
        onChange={handleItemChange}
        data={picklists?.deliveryEventTypes}
      />
    )
  },
  {
    header: "Delivery Event Date",
    accessorKey: "deliveryEventDate",
    cell: (props: CellProps) => (
      <DatePickerCell row={props.row} column={props.column} onChange={handleItemChange} />
    )
  },
  {
    header: "Status",
    accessorKey: "deliveryEventStatus",
    cell: (props: CellProps) => (
      <DropDownListCell
        row={props.row}
        column={props.column}
        onChange={handleItemChange}
        data="STATUS"
      />
    )
  }
];
export const MovementDeliveryEvents = ({
  movementGroup,
  editDisabled,
  onClose
}: MovementDeliveryEventsProps) => {
  const { picklists } = usePicklists();
  const [movementDeliveryEvents, setMovementDeliveryEvents] =
    useState<TMovementDeliveryEventGroup[]>();
  const { loading, error, refetch } = useQuery<Response>(QUERY_MOVEMENT_DELIVERY_EVENTS, {
    fetchPolicy: "no-cache",
    variables: { ids: movementGroup.movements.map(m => m.id) },
    onCompleted: data => onCompleted(data.movements)
  });

  const [updateDeliveryEvents, { loading: loadingDeliveryEvents, error: errorDeliveryEvents }] =
    useMutation<Response>(UPDATE_MOVEMENT_DELIVERY_EVENTS, {
      onCompleted: data => {
        onCompleted(data.movements);
      }
    });

  const onCompleted = (movements: Partial<TMovement[]>) => {
    setMovementDeliveryEvents(transformInput(movements));
  };

  const onReset = () => {
    refetch({ ids: movementGroup.movements.map(m => m.id) });
  };

  const handleItemChange = (updatedRow: TMovementDeliveryEventGroup) => {
    setMovementDeliveryEvents(events =>
      events?.map(event => (event.id === updatedRow.id ? updatedRow : event))
    );
  };

  const onSave = () => {
    if (movementDeliveryEvents) {
      const deliveryEvents = transformOutput(movementDeliveryEvents);
      updateDeliveryEvents({
        variables: {
          deliveryEvents
        }
      });
    }
  };

  const onSaveAndClose = () => {
    onSave();
    onClose();
  };

  if (loading || loadingDeliveryEvents) {
    return <InlineLoadingPanel />;
  }
  if (error) {
    return <ApolloErrorViewer error={error} />;
  }
  if (errorDeliveryEvents) {
    return <ApolloErrorViewer error={errorDeliveryEvents} />;
  }
  return (
    <>
      <Flexbox
        justifyContent="space-between"
        alignItems="center"
        style={{
          width: "100%",
          padding: "8px",
          borderBottom: "1px solid #e0e0e0"
        }}>
        <div>
          <Button
            disabled={editDisabled}
            onClick={onReset}
            style={{ backgroundColor: "#ffc600" }}
            size="small">
            Reset
          </Button>
        </div>

        <Flexbox gap="8px">
          <Button
            onClick={onSave}
            disabled={editDisabled || (movementDeliveryEvents?.length ?? 0) <= 0}
            sentiment="positive"
            size="small">
            Save
          </Button>
          <Button
            onClick={onSaveAndClose}
            disabled={editDisabled || (movementDeliveryEvents?.length ?? 0) <= 0}
            sentiment="positive"
            size="small">
            Save & Close
          </Button>
          <Button size="small" sentiment="negative" onClick={onClose}>
            Close
          </Button>
        </Flexbox>
      </Flexbox>
      <BaseTable
        data={movementDeliveryEvents}
        columns={
          picklists ? makeColumns(picklists, updatedRow => handleItemChange(updatedRow)) : []
        }
        style={{ width: "100%", height: "100%" }}
      />
    </>
  );
};
